import React from 'react';
import moment from 'moment';
import { Badge, Button, Card, CardBody, CardFooter, Col, Input, Label, Row, Table } from 'reactstrap';
import NumberFormat from 'react-number-format';
import classNames from 'classnames';
import { faExclamationCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../../../common/FalconCardHeader';
import FullPagination from '../../../common/pagination/FullPagination';
import { Message, Or, PreloadState } from '../../../UI/preloading/PreloadState';
import { isIterableArray } from '../../../../helpers/utils';
import { ADOPTION_STAGE_LABELS, ADOPTION_STEP_LABELS } from './adoptionLabels';

const managerName = (manager) =>
  `${manager?.firstname || ''} ${manager?.lastname || ''}`.trim() || 'Administrador no registrado';

const AdoptionRow = ({ condominium }) => {
  const { onboarding, planillas, owners, payments, manager } = condominium;

  return (
    <tr>
      <td className="align-middle">
        <div className="font-weight-semi-bold">{condominium.name}</div>
        <div className="text-500">{condominium.identification || condominium.acronym || 'Sin identificación registrada'}</div>
        <div className="text-500">Estado: {condominium.state?.trim() || '-'} · Ciudad: {condominium.city?.trim() || '-'}</div>
      </td>
      <td className="align-middle">
        <div>{managerName(manager)}</div>
        <div className="text-500">{manager?.phone || 'Teléfono no registrado'}</div>
        <div className="text-500 text-truncate" style={{ maxWidth: '14rem' }}>{manager?.email || manager?.username}</div>
      </td>
      <td className="align-middle">
        <Badge color={onboarding.activated ? 'soft-success' : 'soft-warning'} pill>
          {onboarding.completed}/{onboarding.total}
        </Badge>
        {!onboarding.activated && (
          <div className="text-500 mt-1">
            Falta: {onboarding.missing.map((step) => ADOPTION_STEP_LABELS[step] || step).join(', ')}
          </div>
        )}
      </td>
      <td className="align-middle text-center">
        <div className="font-weight-semi-bold">{planillas.total}</div>
        {planillas.last_at && (
          <div className="text-500">Última: {moment(planillas.last_at).format('DD/MM/YYYY')}</div>
        )}
      </td>
      <td className="align-middle text-center">
        <span className="font-weight-semi-bold">{owners.active}</span>
        <span className="text-500"> / {owners.total}</span>
      </td>
      <td className="align-middle text-center font-weight-bold">
        <NumberFormat value={payments.total} displayType="text" thousandSeparator />
      </td>
    </tr>
  );
};

const IndicatorsAdoptionTable = ({
  rows,
  meta,
  loading,
  error,
  stage,
  hasRecords,
  onHasRecordsChange,
  textSearch,
  onTextSearchChange,
  onSearch,
  nextPage,
  previousPage,
  goToPage,
  perPage,
}) => {
  const stageLabel = stage ? ADOPTION_STAGE_LABELS[stage] : null;

  let title = 'Todos los condominios';
  if (stageLabel && hasRecords === 'without') title = `Condominios sin ${stageLabel}`;
  if (stageLabel && hasRecords === 'with') title = `Condominios con ${stageLabel}`;

  return (
    <Card className="mb-3">
      <FalconCardHeader title={title} titleTag="h6" light={false} />
      <CardBody className={classNames('fs--1', { 'p-0': isIterableArray(rows) })}>
        <Row className={classNames('align-items-center', isIterableArray(rows) ? 'm-3' : 'mb-3')}>
          {stage && (
            <Col md={5} lg={4} className="mb-2 mb-md-0 d-flex align-items-center">
              <Label className="mb-0 mr-2 text-nowrap" for="adoption-has-records">Mostrar</Label>
              <Input
                id="adoption-has-records"
                type="select"
                value={hasRecords}
                onChange={({ target }) => onHasRecordsChange(target.value)}
              >
                <option value="without">No la cumplen</option>
                <option value="with">La cumplen</option>
                <option value="all">Todos</option>
              </Input>
            </Col>
          )}
          <Col md={stage ? 7 : 12} lg={stage ? 8 : 12}>
            <div className="d-flex">
              <Input
                type="text"
                value={textSearch}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  onSearch(event.currentTarget.value);
                }}
                onChange={({ target }) => {
                  const value = target.value;
                  onTextSearchChange(value);
                  if (value.trim() === "") onSearch("");
                }}
                placeholder="Nombre, sigla, identificación, estado, ciudad o administrador"
                className="mr-2"
              />
              <Button color="falcon-default" className="text-nowrap" onClick={() => onSearch()}>
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Buscar
              </Button>
            </div>
          </Col>
        </Row>
        <PreloadState cond={isIterableArray(rows)} loading={loading}>
          <Or value={true}>
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th className="align-middle">Condominio</th>
                  <th className="align-middle">Administrador</th>
                  <th className="align-middle">Carga inicial</th>
                  <th className="align-middle text-center">Planillas</th>
                  <th className="align-middle text-center">Propietarios activos</th>
                  <th className="align-middle text-center">Pagos</th>
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((condominium) => (
                  <AdoptionRow key={condominium.id} condominium={condominium} />
                ))}
              </tbody>
            </Table>
          </Or>
          <Or value={false}>
            <Message className="py-3" color={error ? 'danger' : 'info'} icon={faExclamationCircle}>
              {error ? 'Ha ocurrido un error.' : 'No hay condominios para mostrar.'}
            </Message>
          </Or>
        </PreloadState>
      </CardBody>
      {isIterableArray(rows) && (
        <CardFooter className="border-top">
          <FullPagination
            nextPage={nextPage}
            prevPage={previousPage}
            goToPage={goToPage}
            meta={meta}
            className="shadow-none"
            posText="condominios"
            perPage={perPage}
          />
        </CardFooter>
      )}
    </Card>
  );
};

export default IndicatorsAdoptionTable;
